import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import Dashboard from "@/pages/Dashboard";
import ExecutivoMensal from "@/pages/ExecutivoMensal";
import ConferenciaCaixa from "@/pages/ConferenciaCaixa";
import Metas from "@/pages/Metas";
import Receitas from "@/pages/Receitas";
import NovaReceita from "@/pages/NovaReceita";
import EditarReceita from "@/pages/EditarReceita";
import Anexos from "@/pages/Anexos";
import RelatorioLentes from "@/pages/RelatorioLentes";
import Kanban from "@/pages/Kanban";
import Agenda from "@/pages/Agenda";
import Lixeira from "@/pages/Lixeira";
import Login from "@/pages/Login";
import CRM from "@/pages/CRM";
import Estoque from "@/pages/Estoque";
import PremiacaoLentes from "@/pages/PremiacaoLentes";
import Configuracoes from "@/pages/Configuracoes";
import CatalogoAdmin from "@/pages/CatalogoAdmin";
import CatalogoPublico from "@/pages/CatalogoPublico";
import CatalogoLojas from "@/pages/CatalogoLojas";
import AdminPanel from "@/pages/AdminPanel";
import ChangePasswordGate from "@/pages/ChangePasswordGate";
import StoreSetupGate from "@/pages/StoreSetupGate";
import Layout from "@/components/Layout";
import AbaGuard from "@/components/AbaGuard";
import { Toaster } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function App() {
  const [autenticado, setAutenticado] = useState(false);
  const [verificando, setVerificando] = useState(true);
  const [trocarSenha, setTrocarSenha] = useState(false);
  const [onboarding, setOnboarding] = useState(null);

  // Primeiro acesso: permite nomear a única loja da conta
  const checarOnboarding = async () => {
    try {
      const r = await axios.get(`${API}/config/onboarding`);
      setOnboarding(r.data?.pendente ? { loja: r.data.loja } : null);
    } catch {
      setOnboarding(null);
    }
  };

  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem("auth_token");
          localStorage.removeItem("auth_usuario");
          localStorage.removeItem("auth_expira");
          localStorage.removeItem("auth_data_login");
          delete axios.defaults.headers.common["Authorization"];
          setAutenticado(false);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  useEffect(() => {
    const verificarAuth = async () => {
      const token = localStorage.getItem("auth_token");
      const dataLogin = localStorage.getItem("auth_data_login");
      
      if (!token) {
        setAutenticado(false);
        setVerificando(false);
        return;
      }

      // Verificar se é o mesmo dia do login
      const hoje = new Date().toISOString().split("T")[0];
      if (dataLogin !== hoje) {
        // Dia diferente, limpar sessão e pedir login novamente
        localStorage.removeItem("auth_token");
        localStorage.removeItem("auth_usuario");
        localStorage.removeItem("auth_expira");
        localStorage.removeItem("auth_data_login");
        delete axios.defaults.headers.common["Authorization"];
        setAutenticado(false);
        setVerificando(false);
        return;
      }

      // Enviar o token em todas as requisições
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      // Mesmo dia, validar token no servidor
      try {
        const res = await axios.post(`${API}/auth/validate`, { token });
        if (res.data.valid) {
          setAutenticado(true);
          checarOnboarding();
        } else {
          localStorage.removeItem("auth_token");
          localStorage.removeItem("auth_usuario");
          localStorage.removeItem("auth_expira");
          localStorage.removeItem("auth_data_login");
          delete axios.defaults.headers.common["Authorization"];
          setAutenticado(false);
        }
      } catch {
        // Se erro de rede, manter autenticado se for o mesmo dia
        setAutenticado(true);
      } finally {
        setVerificando(false);
      }
    };

    verificarAuth();
  }, []);

  const handleLogin = (data) => {
    // Salvar data do login para verificar se é o mesmo dia
    localStorage.setItem("auth_data_login", new Date().toISOString().split("T")[0]);
    if (data?.token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
    }
    if (data?.tenant_id) localStorage.setItem("auth_tenant_id", data.tenant_id);
    setTrocarSenha(!!data?.must_change_password);
    setAutenticado(true);
    checarOnboarding();
  };

  const handleLogout = () => {
    const token = localStorage.getItem("auth_token");
    if (token) {
      axios.post(`${API}/auth/logout`, { token }).catch(() => {});
    }
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_usuario");
    localStorage.removeItem("auth_expira");
    localStorage.removeItem("auth_data_login");
    delete axios.defaults.headers.common["Authorization"];
    setAutenticado(false);
  };

  const conteudoApp = verificando ? (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4" />
        <p className="text-white/70">Carregando...</p>
      </div>
    </div>
  ) : !autenticado ? (
    <Login onLogin={handleLogin} />
  ) : trocarSenha ? (
    <ChangePasswordGate onDone={() => setTrocarSenha(false)} />
  ) : onboarding ? (
    <StoreSetupGate lojaAtual={onboarding.loja} onDone={() => setOnboarding(null)} />
  ) : (
    <Routes>
      <Route path="/" element={<Layout onLogout={handleLogout} />}>
        <Route index element={<AbaGuard aba="dashboard"><Dashboard /></AbaGuard>} />
        <Route path="executivo-mensal" element={<AbaGuard aba="dashboard"><ExecutivoMensal /></AbaGuard>} />
        <Route path="conferencia-caixa" element={<AbaGuard aba="alertas-caixa"><ConferenciaCaixa /></AbaGuard>} />
        <Route path="metas" element={<AbaGuard aba="metas"><Metas /></AbaGuard>} />
        <Route path="receitas" element={<AbaGuard aba="receitas"><Receitas /></AbaGuard>} />
        <Route path="receitas/nova" element={<AbaGuard aba="nova-receita"><NovaReceita /></AbaGuard>} />
        <Route path="receitas/editar/:id" element={<AbaGuard aba="nova-receita"><EditarReceita /></AbaGuard>} />
        <Route path="receitas/lixeira" element={<AbaGuard aba="receitas"><Lixeira /></AbaGuard>} />
        <Route path="relatorio-lentes" element={<AbaGuard aba="lentes"><RelatorioLentes /></AbaGuard>} />
        <Route path="kanban" element={<AbaGuard aba="mensagens"><Kanban /></AbaGuard>} />
        <Route path="agenda" element={<Agenda />} />
        <Route path="anexos" element={<Anexos />} />
        <Route path="crm" element={<AbaGuard aba="crm"><CRM /></AbaGuard>} />
        <Route path="estoque" element={<AbaGuard aba="estoque"><Estoque /></AbaGuard>} />
        <Route path="premiacao-lentes" element={<PremiacaoLentes />} />
        <Route path="catalogo" element={<AbaGuard aba="catalogo"><CatalogoAdmin /></AbaGuard>} />
        <Route path="configuracoes" element={<Configuracoes />} />
      </Route>
    </Routes>
  );

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/catalogo/:slug" element={<CatalogoPublico />} />
        <Route path="/lojas" element={<CatalogoLojas />} />
        <Route path="/admin" element={<AdminPanel />} />
        <Route path="/*" element={conteudoApp} />
      </Routes>
      <Toaster />
    </BrowserRouter>
  );
}

export default App;
